import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Entity } from 'src/app/classes/entity.class';
import { User } from 'src/app/classes/user.class';
import { AfasService } from 'src/app/services/afas.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { EntityType, EntityTypeCode } from 'src/app/services/entities/entity-type.class';
import { UserService } from 'src/app/services/user/user.service';
import { Settings } from 'src/app/settings.class';
import { Utils } from 'src/app/utils.class';
import { EntityDetailComponent } from './entity-detail/entity-detail.component';

@Component({
    selector: 'app-entities-overview',
    templateUrl: './entities-overview.component.html',
    styleUrls: ['./entities-overview.component.scss'],
    standalone: false,
})
export class EntitiesOverviewComponent implements OnInit, OnDestroy {
    public entities: EntityViewGroup[] = [];
    public entityTypes: Map<string, EntityType>;
    today = new Date();
    Settings = Settings;
    users: User[];
    allEntities: Entity[];
    extraColsMap = new Map<string, number>();
    private subscriptions = new Subscription();
    isSyncing = signal(false);

    constructor(
        public dialogRef: MatDialogRef<EntitiesOverviewComponent>,
        public dialogService: DialogService,
        private entitiesService: EntitiesService,
        private afasService: AfasService,
        private userService: UserService
    ) {}

    showFinished(showFinished: boolean) {
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
        this.subscriptions.add(
            this.entitiesService.getList().subscribe(entities => {
                this.entities = [];
                this.allEntities = entities;
                this.entityTypes.forEach((group, key) => {
                    this.entities.push(
                        new EntityViewGroup(
                            group,
                            entities
                                .filter(entity => !!entity.entitytypes.find(et => et.key === key))
                                .filter(entity => {
                                    return (
                                        showFinished ||
                                        !entity.enddate ||
                                        Utils.getTimeOrNull(entity.enddate) > this.today.getTime()
                                    );
                                })
                                .sort((a, b) => {
                                    const getSorter = (entity: Entity) =>
                                        (entity.use_once ? 0 : 1000) +
                                        entity.order.find(t => t.entitytype_key === key).order;
                                    return getSorter(a) - getSorter(b);
                                })
                        )
                    );
                });
            })
        );
    }

    loadData() {
        this.subscriptions.add(
            this.entitiesService.getTypesMap().subscribe(data => {
                if (AuthorisationService.hasFunctionality(Functionalities.EntitiesManagement)) {
                    this.entityTypes = data;
                } else if (AuthorisationService.hasFunctionality(Functionalities.VkmManagement)) {
                    const entityTypes = data;
                    const allowedTypes = [EntityTypeCode.VkmMateriaal, EntityTypeCode.VkmPreparation] as any;
                    entityTypes.forEach((group, key) => {
                        if (!allowedTypes.includes(key)) {
                            entityTypes.delete(key);
                        }
                    });
                    this.entityTypes = entityTypes;
                }

                this.entityTypes.forEach(entityType => {
                    const extraCols = [
                        !!entityType.has_lowloader,
                        !!entityType.has_truck,
                        !!entityType.has_preferred_team,
                        !!entityType.has_driver,
                    ].filter(p => !!p).length;
                    this.extraColsMap.set(entityType.key, extraCols);
                });
                this.showFinished(false);

                this.subscriptions.add(
                    this.userService.getList().subscribe(users => {
                        this.users = users;
                    })
                );
            })
        );
    }

    ngOnInit() {
        this.loadData();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    drop(event: CdkDragDrop<string[]>, array: EntityViewGroup) {
        moveItemInArray(array.entities, event.previousIndex, event.currentIndex);
        const sortingItems = [] as {
            order;
            entity_id;
            entitytype_key;
        }[];
        array.entities.forEach((item, index) => {
            sortingItems.push({
                order: index,
                entity_id: item.id,
                entitytype_key: item.entitytype_key,
            });
        });
        this.entitiesService.changeOrder(sortingItems, array.type.key).then();
    }

    public openEntity(entity: Entity) {
        this.dialogService.open(EntityDetailComponent, entity);
    }

    public addEntity(type: EntityType) {
        const entity = new Entity();
        entity.entitytypes = [type];
        this.dialogService.open(EntityDetailComponent, entity);
    }

    close() {
        this.dialogRef.close();
    }

    sync() {
        this.isSyncing.set(true);

        this.subscriptions.add(
            this.afasService.afasSync().subscribe({
                next: () => {
                    this.isSyncing.set(false);
                    this.loadData();
                },
                error: err => {
                    this.isSyncing.set(false);
                    console.error('Fout bij AFAS synchronisatie', err);
                },
            })
        );
    }
}

export class EntityViewGroup {
    public type: EntityType;
    public entities: Entity[];

    constructor(type: EntityType, entities: Entity[]) {
        this.type = type;
        this.entities = entities;
    }
}
